// Alert - programmatic fixed alerts/toasts rendered into [data-alert-area].
// Behavioral port of v1 modules/Alert.js (not auto-initialized; consumers call
// `new Alert({...})`).

import { createElementFromString, transitionEnd } from '../utils';

export interface AlertConfig {
  container: string;
  type: 'info' | 'success' | 'warning' | 'error' | string;
  text: string;
  icon?: string;
  loader: boolean;
  closeable: boolean;
  customActionTemplate?: string;
  onAction?: EventListener;
  timeout?: number;
  closeText: string;
}

const defaultConfig: AlertConfig = {
  container: '[data-alert-area]',
  type: 'info',
  text: '',
  icon: undefined,
  loader: false,
  closeable: true,
  customActionTemplate: undefined,
  onAction: undefined,
  timeout: undefined,
  closeText: 'Close',
};

const templateIcon = (icon: string) => `
  <div class="alert__icon">
    <i class="far fa-${icon} fa-xl"></i>
  </div>
`;

const templateLoader = () => `
  <div class="alert__loader">
    <i class="far fa-loader fa-spin fa-xl"></i>
  </div>
`;

const templateAction = (customTemplate?: string, closeText = '') => `
  <div class="alert__action">
    ${customTemplate || `
      <button class="btn btn--clean btn--small" role="button" aria-hidden="true" data-alert-action>
        <i class="far fa-xmark fa-xl"></i>
        <span class="visually-hidden">${closeText}</span>
      </button>
    `}
  </div>
`;

export default class Alert {
  config: AlertConfig;

  container: Element | null = null;

  alert!: HTMLDivElement;

  actionEl: Element | null = null;

  private timeoutRunner?: ReturnType<typeof setTimeout>;

  constructor(config: Partial<AlertConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
    this._init();
  }

  private _init = (): void => {
    this.container = document.querySelector(this.config.container);

    // abort if container does not exist in the DOM
    if (!this.container) {
      return;
    }

    this.alert = document.createElement('div');
    this.alert.classList.add('alert', 'alert--fixed', `alert--${this.config.type}`);

    this.alert.setAttribute('role', this.config.loader ? 'status' : 'alert');

    if (this.config.icon) {
      this.alert.appendChild(createElementFromString(templateIcon(this.config.icon)));
    }

    if (this.config.loader) {
      this.alert.appendChild(createElementFromString(templateLoader()));
    }

    const alertContent = document.createElement('div');
    alertContent.className = 'alert__content';
    alertContent.innerHTML = this.config.text;
    this.alert.appendChild(alertContent);

    if (this.config.closeable || this.config.customActionTemplate) {
      this.alert.appendChild(
        createElementFromString(
          templateAction(this.config.customActionTemplate, this.config.closeText),
        ),
      );
    }

    this.container.prepend(this.alert);

    // wait for insert to dom, then attach class which can bring in animation
    setTimeout(() => {
      this.alert.classList.add('alert--fixed-active');
    }, 100);

    // attach click event on alert action if alert is closeable or custom action is defined
    if (this.config.closeable || this.config.customActionTemplate) {
      this.actionEl = this.alert.querySelector('[data-alert-action]');

      if (!this.actionEl) {
        return;
      }

      this.actionEl.addEventListener('click', this.config.onAction || this.destroy);
    }

    // start timer if timeout is defined
    if (this.config.timeout) {
      this._setTimeout();

      // clear timer if mouse is hovering above alert
      this.alert.addEventListener('mouseenter', this._clearTimeout);
    }

    document.addEventListener('keydown', this._handleEscape);
  };

  private _setTimeout = (): void => {
    this.timeoutRunner = setTimeout(this.destroy, this.config.timeout);
  };

  private _clearTimeout = (): void => {
    clearTimeout(this.timeoutRunner);
    this.alert.addEventListener('mouseleave', this._setTimeout);
  };

  private _handleEscape = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.destroy();
    }
  };

  destroy = (): void => {
    if (this.actionEl) {
      this.actionEl.removeEventListener('click', this.config.onAction || this.destroy);
    }

    if (this.config.timeout) {
      this.alert.removeEventListener('mouseenter', this._clearTimeout);
    }

    document.removeEventListener('keydown', this._handleEscape);

    // wait for animation to end and then remove instance from DOM
    this.alert.addEventListener(
      transitionEnd,
      () => {
        if (this.container && this.container.contains(this.alert)) {
          this.container.removeChild(this.alert);
        }
      },
      { once: true },
    );

    // animation could be attached on this class
    this.alert.classList.add('alert--destroy');
  };
}
